import { useState } from 'react'
import {
  Alert,
  Button,
  Fade,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { Modal } from '../../../components'
import { ModalRegisterProps } from './@types'
import { useInputText } from '../../../hooks'
import { EnumAccessLevel } from '../../../@types'
import { useData } from './use-data'
import services from '../../../services'

export default function ModalRegister({ open, onClose }: ModalRegisterProps) {
  const { ROLES, refetch } = useData()

  const initialValues = { email: '', name: '', surname: '' }
  const input = useInputText(initialValues)

  const [accessLevel, setAccessLevel] = useState<EnumAccessLevel>('TEACHER')
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const onAccessLevelChange = (event: SelectChangeEvent) =>
    setAccessLevel(event.target.value.toUpperCase() as EnumAccessLevel)

  const closeModal = () => {
    refetch()
    input.clear()
    setAccessLevel('TEACHER')
    setError(null)
    onClose()
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    try {
      event.preventDefault()
      if (error) setError(null)
      setLoading(true)
      const { email, name, surname } = input
      const password = `Win_${name.toLowerCase()}_${new Date().getFullYear()}`

      await services.user.create({
        accessLevel,
        email,
        name,
        password,
        surname,
      })

      closeModal()
    } catch (error) {
      setError((error as Error).message)
    } finally {
      refetch()
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={closeModal} head={<h2>Register new user</h2>}>
      <Fade in={open}>
        <div className="text-xl grid gap-8">
          {loading && <Alert severity="warning">Sending data...</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="grid gap-4">
              <TextField
                autoFocus
                required
                label="First name"
                name="name"
                value={input.name}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.name}
                helperText={input.errors.name}
                disabled={loading}
              />

              <TextField
                required
                label="Last name"
                name="surname"
                value={input.surname}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.surname}
                helperText={input.errors.surname}
                disabled={loading}
              />
            </div>

            <div className="grid gap-4">
              <TextField
                required
                label="E-mail"
                type="email"
                name="email"
                value={input.email}
                onChange={input.onChange}
                onBlur={input.onBlur}
                error={!!input.errors.email}
                helperText={input.errors.email}
                disabled={loading}
              />

              <Select
                value={accessLevel}
                onChange={onAccessLevelChange}
                disabled={loading}
              >
                {ROLES.map((role) => (
                  <MenuItem
                    disabled={role === 'STUDENT' || loading}
                    key={role}
                    value={role}
                  >
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </div>

            <div className="w-max max-w-full ml-auto mt-4 gap-4 flex">
              <Button
                disabled={loading}
                variant="outlined"
                type="reset"
                onClick={closeModal}
              >
                Cancel
              </Button>
              <Button disabled={loading} type="submit">
                Submit
              </Button>
            </div>
          </form>
        </div>
      </Fade>
    </Modal>
  )
}
